import React, { useEffect, useState } from 'react';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import StudioConfigurationView from './StudioConfigurationView';
import './PaywallOverlay.css';

// Solo and Studio are the two subscription tiers.
// Annual pricing is monthly × 10 ("2 months free").
const PLANS = [
  {
    id: 'solo',
    name: 'Solo',
    tagline: 'For independent designers',
    monthly: 59,
    features: [
      '1 user',
      'Unlimited projects',
      'Full business-management tools',
      'Standard support',
    ],
  },
  {
    id: 'studio',
    name: 'Studio',
    tagline: 'For collaborative studios',
    monthly: 99,
    features: [
      'Up to 7 users',
      'Unlimited projects',
      'Team collaboration & permissions',
      'Priority support',
    ],
    recommended: true,
  },
];

const SUPPORT_HREF = 'mailto:support@atelierstudio.us';

// Read + strip the ?studio_config= query param once, so we can render the
// paid-thanks view (or return-from-config) without leaving the param in
// the URL bar to fire again on refresh.
const readStudioConfigParam = () => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const val = params.get('studio_config');
  const sessionId = params.get('session_id');
  if (val) {
    params.delete('studio_config');
    params.delete('session_id');
    const q = params.toString();
    const url = window.location.pathname + (q ? `?${q}` : '') + window.location.hash;
    window.history.replaceState({}, '', url);
  }
  return val ? { state: val, sessionId } : null;
};

const PaywallOverlay = ({ user }) => {
  const {
    subscription,
    limits,
    usage,
    isExpired,
    isOverLimit,
    showPaywall,
    activateSubscription,
    dismissPaywall,
  } = useSubscriptionContext();

  const [billing, setBilling] = useState(subscription?.billing_period === 'annual' ? 'annual' : 'monthly');
  const [activatingPlan, setActivatingPlan] = useState(null);
  const [successPlan, setSuccessPlan] = useState(null);
  const [view, setView] = useState('plans'); // 'plans' | 'config' | 'paid' | 'cancelled'
  const [configVerifyMsg, setConfigVerifyMsg] = useState(null);

  // Detect return from Stripe on mount — Stripe redirects back to /?studio_config=paid&session_id=…
  useEffect(() => {
    const flag = readStudioConfigParam();
    if (!flag) return;
    if (flag.state === 'paid') {
      setView('paid');
      // Confirm with the backend (marks the row paid + returns the config).
      if (flag.sessionId) {
        fetch('/api/chat/studio-config-checkout.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'verify', session_id: flag.sessionId }),
        })
          .then(r => r.json())
          .then(data => {
            if (data.success && data.paid) {
              setConfigVerifyMsg(null);
            } else if (data.success && !data.paid) {
              setConfigVerifyMsg('Payment is not yet confirmed by Stripe. Please refresh in a moment.');
            } else {
              setConfigVerifyMsg(data.message || 'Could not verify payment.');
            }
          })
          .catch(() => setConfigVerifyMsg('Could not verify payment (network error).'));
      }
    } else if (flag.state === 'cancelled') {
      setView('cancelled');
    }
  }, []);

  if (!showPaywall && !isOverLimit && view === 'plans') return null;

  const handleChoose = async (planId) => {
    setActivatingPlan(planId);
    try {
      await activateSubscription(planId, billing);
      setSuccessPlan(planId);
      setTimeout(() => setSuccessPlan(null), 2000);
    } catch (err) {
      console.error('Activation failed:', err);
    } finally {
      setActivatingPlan(null);
    }
  };

  const isTrialExpired = isExpired && subscription?.plan_type === 'trial';
  const isPaidExpired = isExpired && subscription?.plan_type !== 'trial';

  const heading = isTrialExpired
    ? 'Your Trial Has Expired'
    : isPaidExpired
    ? 'Subscription Expired'
    : isOverLimit
    ? 'Resource Limit Reached'
    : 'Choose a Plan';

  const message = isTrialExpired
    ? 'Your 14-day free trial has ended. Choose a plan to continue using Atelier Studio.'
    : isPaidExpired
    ? 'Your subscription has expired. Choose a plan to continue using Atelier Studio.'
    : isOverLimit
    ? `You've reached ${usage?.total_records}/${limits?.max_records} records or ${usage?.active_users}/${limits?.max_users} users. Upgrade to increase your limits.`
    : 'Choose a plan to continue using Atelier Studio.';

  // Annual = monthly × 10 (two months free). We show the effective per-month
  // price so the two tabs read as comparable rates.
  const displayPrice = (plan) => (billing === 'annual' ? Math.round(plan.monthly * 10 / 12) : plan.monthly);

  // ---- Alternate views (Studio Configuration flow, return-from-Stripe) ----
  if (view === 'config') {
    return (
      <div className="paywall-overlay">
        <div className="paywall-modal">
          <StudioConfigurationView user={user} onBack={() => setView('plans')} />
        </div>
      </div>
    );
  }
  if (view === 'paid') {
    return (
      <div className="paywall-overlay">
        <div className="paywall-modal">
          <div className="sc-paid">
            <div className="sc-paid-icon" aria-hidden="true">✓</div>
            <h2 className="paywall-title">Payment received</h2>
            <p className="paywall-message">
              Thanks — your Studio Configuration is scheduled. A setup specialist will
              be in touch within one business day to schedule your discovery call.
            </p>
            {configVerifyMsg && (
              <p className="paywall-message" style={{ marginTop: '0.75rem', color: '#f2c0bd' }}>
                {configVerifyMsg}
              </p>
            )}
            <div className="sc-cta-row" style={{ justifyContent: 'center' }}>
              <button type="button" className="sc-primary" onClick={() => setView('plans')}>
                Back to plans
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (view === 'cancelled') {
    return (
      <div className="paywall-overlay">
        <div className="paywall-modal">
          <div className="sc-status">
            <div className="sc-status-icon" aria-hidden="true">↩</div>
            <h2 className="paywall-title">Payment cancelled</h2>
            <p className="paywall-message">
              No charge was made. You can resume your configuration at any time.
            </p>
            <div className="sc-cta-row" style={{ justifyContent: 'center' }}>
              <button type="button" className="sc-secondary" onClick={() => setView('plans')}>Back to plans</button>
              <button type="button" className="sc-primary" onClick={() => setView('config')}>Resume configuration</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="paywall-overlay">
      <div className="paywall-modal">
        <div className="paywall-header">
          <div className="paywall-icon" aria-hidden="true">{isExpired ? '⏰' : '🔒'}</div>
          <h2 className="paywall-title">{heading}</h2>
          <p className="paywall-message">{message}</p>

          {subscription?.plan_type === 'trial' && subscription?.days_remaining > 0 && (
            <div className="paywall-trial-badge">
              {subscription.days_remaining} days remaining in trial
            </div>
          )}

          <div className="paywall-billing-toggle" role="tablist" aria-label="Billing period">
            <button
              type="button"
              role="tab"
              aria-selected={billing === 'monthly'}
              className={`paywall-billing-tab ${billing === 'monthly' ? 'active' : ''}`}
              onClick={() => setBilling('monthly')}
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={billing === 'annual'}
              className={`paywall-billing-tab ${billing === 'annual' ? 'active' : ''}`}
              onClick={() => setBilling('annual')}
            >
              Annual · 2 months free
            </button>
          </div>
        </div>

        <div className="paywall-plans">
          {PLANS.map((plan) => {
            const isActivating = activatingPlan === plan.id;
            const isSuccess = successPlan === plan.id;
            return (
              <div
                key={plan.id}
                className={`paywall-plan-card ${plan.recommended ? 'recommended' : ''}`}
              >
                {plan.recommended && <span className="paywall-recommended-badge">Recommended</span>}
                <h3 className="paywall-plan-name">{plan.name}</h3>
                <div className="paywall-price">
                  <span className="paywall-price-amount">${displayPrice(plan)}</span>
                  <span className="paywall-price-period">/mo</span>
                </div>
                <p className="paywall-plan-tagline">{plan.tagline}</p>
                <ul className="paywall-features">
                  {plan.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className={`paywall-choose-btn ${plan.recommended ? 'primary' : ''}`}
                  onClick={() => handleChoose(plan.id)}
                  disabled={activatingPlan !== null}
                >
                  {isActivating ? 'Activating…' : isSuccess ? 'Activated!' : `Choose ${plan.name}`}
                </button>
              </div>
            );
          })}
        </div>

        <div className="paywall-addon">
          <div className="paywall-addon-icon" aria-hidden="true">{'⚙️'}</div>
          <div className="paywall-addon-body">
            <h4 className="paywall-addon-title">Studio Configuration</h4>
            <p className="paywall-addon-desc">
              One-on-one workflow consultation and supported account configuration.
            </p>
          </div>
          <div className="paywall-addon-price">$400 one-time</div>
          <button
            type="button"
            className="paywall-addon-cta"
            onClick={() => setView('config')}
          >
            Learn more
          </button>
        </div>

        {!isExpired && isOverLimit && (
          <button className="paywall-dismiss-btn" onClick={dismissPaywall}>
            Continue with limits
          </button>
        )}

        <div className="paywall-support">
          <a href={SUPPORT_HREF}>Contact support</a>
        </div>
      </div>
    </div>
  );
};

export default PaywallOverlay;
